"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import VerificationModal from "./VerificationModal";
import { requestAdminLoginCode, getErrorMessage as getAdminErrorMessage, isCredentialsError as isAdminCredentialsError, isAccountLockedError } from "../utils/adminSignInApi";

interface SignUpModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginClick?: () => void;
}

export default function SignUpModal({ isOpen, onClose, onLoginClick }: SignUpModalProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [selectedRole, setSelectedRole] = useState("admin");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showVerification, setShowVerification] = useState(false);

  const roles = [
    { value: "admin", label: "Admin", description: "Manage predictions and users" },
    { value: "super-admin", label: "Super Admin", description: "Full system administration" }
  ];

  // Detect mobile screen
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    // Admin validation only
    if (!email) {
      setError("Please enter your email address");
      return;
    }
    
    if (!email.includes('@')) {
      setError("Please enter a valid email address");
      return;
    }

    if (!password || password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Admin login flow - make API call to admin server
      const response = await requestAdminLoginCode(email, password);
      console.log("Admin verification code sent:", response.message);
      
      // Show verification modal on success
      setShowVerification(true);
    } catch (error) {
      console.error("🚨 [SIGNUP] Login error:", error);
      
      // Handle admin-specific errors  
      if (isAdminCredentialsError(error)) {
        setError("Invalid email or password. Please check your credentials.");
      } else if (isAccountLockedError(error)) {
        setError(getAdminErrorMessage(error)); // This will show the specific lockout message
      } else {
        setError(getAdminErrorMessage(error));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerificationClose = () => {
    setShowVerification(false);
    onClose();
  };

  const selectedRoleData = roles.find(role => role.value === selectedRole);

  // If showing verification, render the verification modal (for admins only)
  if (showVerification) {
    return (
      <VerificationModal
        isOpen={showVerification}
        onClose={handleVerificationClose}
        email={email}
        role={selectedRole}
        password={password} // Pass password to verification modal for admin roles
      />
    );
  }

  // Otherwise render the signup modal
  return (
    <>
      {/* Backdrop - matching KOM theme */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
        onClick={onClose}
      ></div>
      
      {/* Modal */}
      <div className={`fixed z-50 pointer-events-none ${
        isMobile 
          ? 'inset-x-0 top-[77px] flex justify-center' 
          : 'left-1/2 top-[77px] transform -translate-x-1/2'
      }`}>
        <div 
          className={`bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f1419] rounded-2xl border border-[#CB6CE6]/20 pointer-events-auto relative shadow-2xl shadow-[#CB6CE6]/10 ${
            isMobile 
              ? 'w-[90%] max-h-[80vh] overflow-y-auto' 
              : 'w-[420px]'
          }`} 
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button 
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 border border-white/10 transition-all duration-300 z-10 group"
            onClick={onClose}
            aria-label="Close"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1L13 13M1 13L13 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="group-hover:stroke-[#CB6CE6] transition-colors"/>
            </svg>
          </button>
          
          <div className={`relative ${isMobile ? 'px-6 py-6' : 'px-8 py-8'}`}>
            {/* Error message */}
            {error && (
              <div className="absolute top-0 left-0 right-0 flex justify-center">
                <div className="bg-red-900/30 border border-red-500/50 rounded-full px-4 py-2 flex items-center space-x-2 shadow-lg translate-y-[-50%] max-w-[90%] backdrop-blur-sm">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 4V9" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8 11.5V12" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14Z" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span className="text-red-400 text-sm font-medium">{error}</span>
                </div>
              </div>
            )}
            
            {/* Logo with glow effect */}
            <div className="relative mb-4 flex items-center space-x-3">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-[#CB6CE6] to-[#6B21A8] rounded-lg blur-xl opacity-20 w-10 h-10"></div>
                <Image
                  src="/phoebe-logo.png"
                  alt="Phoebe Logo"
                  width={32}
                  height={32}
                  className="object-contain relative z-10 drop-shadow-lg"
                />
              </div>
              <div>
                <h2 className={`font-black text-transparent bg-clip-text bg-gradient-to-r from-[#CB6CE6] via-white to-[#6B21A8] ${
                  isMobile ? 'text-xl' : 'text-2xl'
                }`}>
                  Access Portal
                </h2>
                <p className="text-[#9CA3AF] text-sm">
                  Enter your credentials to access the platform
                </p>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Role Selection Dropdown */}
              <div className="relative">
                <label className="block text-white font-medium mb-2 text-sm">
                  Select Role
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="w-full bg-black/30 border border-[#CB6CE6]/30 rounded-xl px-4 py-3 text-left text-white hover:border-[#CB6CE6]/50 transition-all duration-300 focus:outline-none focus:border-[#CB6CE6] backdrop-blur-sm"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-white text-sm">{selectedRoleData?.label}</div>
                        <div className="text-xs text-[#9CA3AF] mt-0.5">{selectedRoleData?.description}</div>
                      </div>
                      <svg 
                        className={`w-4 h-4 text-[#CB6CE6] transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>
                  
                  {isDropdownOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-[#1a1a2e] border border-[#CB6CE6]/30 rounded-xl shadow-2xl z-20 overflow-hidden backdrop-blur-sm">
                      {roles.map((role) => (
                        <button
                          key={role.value}
                          type="button"
                          onClick={() => {
                            setSelectedRole(role.value);
                            setIsDropdownOpen(false);
                          }}
                          className={`w-full px-4 py-3 text-left hover:bg-[#CB6CE6]/10 transition-colors border-b border-white/5 last:border-b-0 ${
                            selectedRole === role.value ? 'bg-[#CB6CE6]/20' : ''
                          }`}
                        >
                          <div className="font-medium text-white text-sm">{role.label}</div>
                          <div className="text-xs text-[#9CA3AF] mt-0.5">{role.description}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Email Input */}
              <div>
                <label className="block text-white font-medium mb-2 text-sm">
                  Admin Email Address
                </label>
                <div className="relative">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="w-full bg-black/30 border border-[#CB6CE6]/30 rounded-xl px-4 py-3 text-white placeholder-[#9CA3AF] hover:border-[#CB6CE6]/50 focus:border-[#CB6CE6] focus:outline-none transition-all duration-300 backdrop-blur-sm text-sm"
                    required
                  />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 8L10.89 13.26C11.2187 13.4793 11.6049 13.5963 12 13.5963C12.3951 13.5963 12.7813 13.4793 13.11 13.26L21 8M5 19H19C19.5304 19 20.0391 18.7893 20.4142 18.4142C20.7893 18.0391 21 17.5304 21 17V7C21 6.46957 20.7893 5.96086 20.4142 5.58579C20.0391 5.21071 19.5304 5 19 5H5C4.46957 5 3.96086 5.21071 3.58579 5.58579C3.21071 5.96086 3 6.46957 3 7V17C3 17.5304 3.21071 18.0391 3.58579 18.4142C3.96086 18.7893 4.46957 19 5 19Z" stroke="#CB6CE6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
              </div>

              {/* Password Input */}
              <div>
                <label className="block text-white font-medium mb-2 text-sm">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full bg-black/30 border border-[#CB6CE6]/30 rounded-xl px-4 py-3 pr-10 text-white placeholder-[#9CA3AF] hover:border-[#CB6CE6]/50 focus:border-[#CB6CE6] focus:outline-none transition-all duration-300 backdrop-blur-sm text-sm"
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#CB6CE6] hover:text-[#CB6CE6]/80 transition-colors"
                  >
                    {showPassword ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17.94 17.94C16.2306 19.243 14.1491 19.9649 12 20C5 20 1 12 1 12C2.24389 9.68192 4.028 7.66032 6.17 6.17M9.9 4.24C10.5883 4.0789 11.2931 3.99836 12 4C19 4 23 12 23 12C22.393 13.1356 21.6691 14.2048 20.84 15.19M14.12 14.12C13.8454 14.4148 13.5141 14.6512 13.1462 14.8151C12.7782 14.9791 12.3809 15.0673 11.9781 15.0744C11.5753 15.0815 11.1749 15.0074 10.8016 14.8565C10.4283 14.7056 10.0887 14.481 9.80385 14.1962C9.51900 13.9113 9.29439 13.5717 9.14351 13.1984C8.99264 12.8251 8.91853 12.4247 8.92563 12.0219C8.93274 11.6191 9.02091 11.2218 9.18488 10.8538C9.34884 10.4858 9.58525 10.1546 9.88 9.88M1 1L23 23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </button>
                </div>
                <p className="text-[#9CA3AF] text-xs mt-1">
                  Password must be at least 8 characters long
                </p>
              </div>

              {/* Continue Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-[#CB6CE6] to-[#6B21A8] text-white py-3 rounded-xl font-bold text-base hover:shadow-2xl hover:shadow-[#CB6CE6]/30 transform hover:scale-[1.02] transition-all duration-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none relative overflow-hidden group"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                    Verifying...
                  </div>
                ) : (
                  <span className="relative z-10">
                    Verify & Continue
                  </span>
                )}
                
                {/* Button shine effect */}
                <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-1000 ease-out"></div>
              </button>
            </form>
            
            {/* Help Text */}
            <div className="mt-4 text-center">
              <p className="text-[#9CA3AF] text-xs leading-relaxed">
                Admin access requires valid credentials. <span className="text-[#CB6CE6] font-medium">Contact system administrator</span> for assistance.
              </p>
            </div>
            
            {/* Cancel button for mobile */}
            {isMobile && (
              <button 
                className="mt-3 w-full py-2 text-[#9CA3AF] text-sm font-medium border border-white/10 rounded-xl hover:bg-white/5 transition-all duration-300"
                onClick={onClose}
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}